import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { GAME_PASSPORT_EVENT, mutateGamePassport, readGamePassport, reconcileGamePassport } from './game-passport';
import type { Fixture, FixtureSource } from './types';

/** Reuses the already mounted fixture feed. Wallet actions never fetch. */
export function useGamePassport(fixtures: Fixture[], season: string, source?: FixtureSource, stale = false, complete = true) {
  const [revision, refresh] = useReducer((n: number) => n + 1, 0);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const input = JSON.stringify([fixtures, season, source, stale, complete]);
  const state = useMemo(() => {
    const [matches, year, origin, isStale, wholeBook] = JSON.parse(input) as [Fixture[], string, FixtureSource | undefined, boolean, boolean];
    return reconcileGamePassport(readGamePassport(), matches, year, origin, isStale, wholeBook);
  }, [input, revision]);
  useEffect(() => {
    const notify = () => refresh();
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener(GAME_PASSPORT_EVENT, notify);
    window.addEventListener('coyg:participation-changed', notify);
    window.addEventListener('coyg:local-data-cleared', notify);
    window.addEventListener('storage', notify);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener(GAME_PASSPORT_EVENT, notify);
      window.removeEventListener('coyg:participation-changed', notify);
      window.removeEventListener('coyg:local-data-cleared', notify);
      window.removeEventListener('storage', notify);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);
  useEffect(() => {
    let active = true;
    // Other mounted wallets may have committed since render; merge at commit.
    const [matches, year, origin, isStale, wholeBook] = JSON.parse(input) as [Fixture[], string, FixtureSource | undefined, boolean, boolean];
    void mutateGamePassport((current) => reconcileGamePassport(current, matches, year, origin, isStale, wholeBook))
      .then((ok) => { if (active) setStorageAvailable(ok); });
    return () => { active = false; };
  }, [state, input]);
  const keepTicket = useCallback(async (id: string) => {
    if (!state.tickets.some((ticket) => ticket.fixture.id === id)) return false;
    const ok = await mutateGamePassport((current) => ({ ...current, tickets: current.tickets.map((ticket) => ticket.fixture.id === id ? { ...ticket, saved: true } : ticket) }));
    setStorageAvailable(ok); return ok;
  }, [state]);
  const discover = useCallback(async (id: string) => {
    if (!/^[a-z0-9-]{1,100}$/.test(id)) return false;
    const ok = await mutateGamePassport((current) => ({ ...current, discoveries: [...current.discoveries, id] }));
    setStorageAvailable(ok); return ok;
  }, []);
  const resetDiscoveries = useCallback(async () => {
    const ok = await mutateGamePassport((current) => ({ ...current, discoveries: [] }));
    setStorageAvailable(ok); return ok;
  }, []);
  return { tickets: state.tickets, discoveries: state.discoveries, storageAvailable, keepTicket, discover, resetDiscoveries };
}
